import { isEqual } from "lodash";
import { useCallback, useEffect } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  useCustomQueryParams,
  useDynamicFormsUtils,
  usePageContext,
} from "@ryde/redux";
import {
  activeViewIdAtomFamily,
  checkIsAllInit,
  checkIsReadyToInitActiveView,
  checkIsReadyToInitFinalSchema,
  companyIdSelector,
  defaultViewSF,
  finalSchemaInitStateAF,
  getLastFinalSchemaLocalStore,
  gridSchemaSF,
  isColumnsSizingDiffFromView,
  isFinalSchemaValid,
  isProfileScopeLoadedSelector,
  isSchemaRootLoadedSF,
  isViewLoadedSF,
  resolveColumnsGrouping,
  resolveColumnsOrder,
  resolveColumnsSorting,
  resolveColumnsVisibility,
  resolveFilterSchema,
  setFinalSchemaLocalStore,
  tableFiltersSchemaAtomFamily,
  type TValidFinalSchema,
} from "@ryde/shared";

export const useFinalSchemaService = () => {
  const { entity } = usePageContext();
  const companyId = useRecoilValue(companyIdSelector);
  const isViewLoaded = useRecoilValue(isViewLoadedSF(entity));
  const isProfileScopeLoaded = useRecoilValue(isProfileScopeLoadedSelector);
  const isSchemaRootLoaded = useRecoilValue(isSchemaRootLoadedSF(entity));
  const defaultView = useRecoilValue(defaultViewSF(entity));
  const [activeViewId, setActiveViewId] = useRecoilState(
    activeViewIdAtomFamily(entity),
  );
  const { filtersSchemaFromQs, tableSortingFromQS, tableGroupingFromQS } =
    useCustomQueryParams();
  const { columnsOrder, columnsVisibility, sortBy, groupBy, defaultFilters } =
    useRecoilValue(gridSchemaSF(entity));
  const { getGridFieldNameByControlId } = useDynamicFormsUtils();
  const setTableFilters = useSetRecoilState(
    tableFiltersSchemaAtomFamily(entity),
  );
  const [finalSchemaInitState, setFinalSchemaInitState] = useRecoilState(
    finalSchemaInitStateAF(entity),
  );

  const isSomethingFromQueryString =
    Boolean(filtersSchemaFromQs) &&
    Boolean(tableSortingFromQS) &&
    Boolean(tableGroupingFromQS);

  const resolve = useCallback(() => {
    if (!companyId) {
      return;
    }

    const localStoredFinalSchema = getLastFinalSchemaLocalStore(
      companyId,
      entity,
    );
    const isValid = isFinalSchemaValid(localStoredFinalSchema);

    if (isValid) {
      const finalSchemaStored: TValidFinalSchema = {
        filtersSchema: {},
        columnsOrder: [],
        columnsGrouping: [],
        columnsVisibility: {},
        columnsSorting: [],
        filtersCriteria: {},
        columnsSizing: {},
      };
      // filters
      finalSchemaStored.filtersSchema = resolveFilterSchema({
        filtersSchemaFromQs,
        localStoredFinalSchemaFiltersSchema:
          localStoredFinalSchema?.filtersSchema,
        entity,
      });
      // sorting
      finalSchemaStored.columnsSorting = resolveColumnsSorting({
        tableSortingFromQS,
        localStoredFinalSchemaColumnsSorting:
          localStoredFinalSchema?.columnsSorting,
        getGridFieldNameByControlId,
      });
      // grouping
      finalSchemaStored.columnsGrouping = resolveColumnsGrouping({
        tableGroupingFromQS,
        localStoredFinalSchemaColumnsGrouping:
          localStoredFinalSchema?.columnsGrouping,
        viewColumnsGrouping: defaultView?.viewState?.columnsGrouping,
        groupBy,
        getGridFieldNameByControlId,
      });
      setFinalSchemaInitState(prev => ({
        ...prev,
        isFinalSchemaSet: true,
      }));
      return;
    }

    const finalSchema: TValidFinalSchema = {
      filtersSchema: {},
      columnsOrder: [],
      columnsGrouping: [],
      columnsVisibility: {},
      columnsSorting: [],
      filtersCriteria: {},
      columnsSizing: {},
    };

    // filters
    finalSchema.filtersSchema = resolveFilterSchema({
      filtersSchemaFromQs,
      viewFilterSchema: defaultView?.viewState?.filtersSchema,
      defaultFilters,
      entity,
    });
    // column order
    finalSchema.columnsOrder = resolveColumnsOrder({
      viewColumnsOrder: defaultView?.viewState?.columnsOrder,
      columnsOrder,
      getGridFieldNameByControlId,
    });
    // visibility
    finalSchema.columnsVisibility = resolveColumnsVisibility({
      viewColumnsVisibility: defaultView?.viewState?.columnsVisibility,
      columnsVisibility,
      getGridFieldNameByControlId,
    });
    // widths
    finalSchema.columnsSizing = defaultView?.viewState?.columnsSizing ?? {};
    // sorting
    finalSchema.columnsSorting = resolveColumnsSorting({
      tableSortingFromQS,
      viewColumnsSorting: defaultView?.viewState?.columnsSorting,
      sortBy,
      getGridFieldNameByControlId,
    });
    // grouping
    finalSchema.columnsGrouping = resolveColumnsGrouping({
      tableGroupingFromQS,
      viewColumnsGrouping: defaultView?.viewState?.columnsGrouping,
      groupBy,
      getGridFieldNameByControlId,
    });

    setFinalSchemaInitState(prev => ({
      ...prev,
      isFinalSchemaSet: true,
    }));
    setFinalSchemaLocalStore({ companyId, page: entity, finalSchema });
  }, [
    columnsOrder,
    columnsVisibility,
    companyId,
    defaultFilters,
    defaultView?.viewState?.columnsGrouping,
    defaultView?.viewState?.columnsOrder,
    defaultView?.viewState?.columnsSizing,
    defaultView?.viewState?.columnsSorting,
    defaultView?.viewState?.columnsVisibility,
    defaultView?.viewState?.filtersSchema,
    entity,
    filtersSchemaFromQs,
    getGridFieldNameByControlId,
    groupBy,
    setFinalSchemaInitState,
    sortBy,
    tableGroupingFromQS,
    tableSortingFromQS,
  ]);

  const resolveActiveView = useCallback(() => {
    if (activeViewId) {
      return;
    }

    if (defaultView && !isSomethingFromQueryString) {
      let isFinalSchemaDifferentFromView = false;

      if (companyId) {
        const localStoredFinalSchema = getLastFinalSchemaLocalStore(
          companyId,
          entity,
        );

        const isValid = isFinalSchemaValid(localStoredFinalSchema);

        if (isValid) {
          const isColumnsVisibilityDiff = !isEqual(
            localStoredFinalSchema?.columnsVisibility,
            defaultView.viewState?.columnsVisibility,
          );
          const isColumnsGroupingDiff = !isEqual(
            localStoredFinalSchema?.columnsGrouping,
            defaultView.viewState?.columnsGrouping,
          );
          const isColumnsOrderDiff = !isEqual(
            localStoredFinalSchema?.columnsOrder,
            defaultView.viewState?.columnsOrder,
          );
          const isColumnsSizingDiff = isColumnsSizingDiffFromView({
            columnsSizing: localStoredFinalSchema?.columnsSizing,
            viewColumnsSizing: defaultView.viewState?.columnsSizing,
          });

          isFinalSchemaDifferentFromView =
            isColumnsVisibilityDiff ||
            isColumnsGroupingDiff ||
            isColumnsOrderDiff ||
            isColumnsSizingDiff;
        }
      }

      if (!isFinalSchemaDifferentFromView) {
        setActiveViewId(defaultView.id);
      }
    }

    setFinalSchemaInitState(prev => ({
      ...prev,
      isActiveViewResolved: true,
    }));
  }, [
    activeViewId,
    companyId,
    defaultView,
    entity,
    isSomethingFromQueryString,
    setActiveViewId,
    setFinalSchemaInitState,
  ]);

  // View init watcher
  useEffect(() => {
    if (finalSchemaInitState.isViewSet) {
      return;
    }

    if (isViewLoaded) {
      setFinalSchemaInitState(prev => ({
        ...prev,
        isViewSet: true,
      }));
    }
  }, [finalSchemaInitState.isViewSet, isViewLoaded, setFinalSchemaInitState]);

  // Root schema init watcher
  useEffect(() => {
    if (finalSchemaInitState.isRootSchemaSet) {
      return;
    }

    if (isSchemaRootLoaded) {
      setFinalSchemaInitState(prev => ({
        ...prev,
        isRootSchemaSet: true,
      }));
    }
  }, [
    finalSchemaInitState.isRootSchemaSet,
    isSchemaRootLoaded,
    setFinalSchemaInitState,
  ]);

  // Profile init watcher
  useEffect(() => {
    if (finalSchemaInitState.isProfileScopeSet) {
      return;
    }

    if (isProfileScopeLoaded) {
      setFinalSchemaInitState(prev => ({
        ...prev,
        isProfileScopeSet: true,
      }));
    }
  }, [
    finalSchemaInitState.isProfileScopeSet,
    isProfileScopeLoaded,
    setFinalSchemaInitState,
  ]);

  // final local store
  useEffect(() => {
    if (finalSchemaInitState.isFinalSchemaSet) {
      return;
    }

    if (!checkIsReadyToInitFinalSchema(finalSchemaInitState)) {
      return;
    }

    resolve();
  }, [finalSchemaInitState, resolve]);

  // activate default view
  useEffect(() => {
    if (finalSchemaInitState.isActiveViewResolved) {
      return;
    }

    if (!checkIsReadyToInitActiveView(finalSchemaInitState)) {
      return;
    }

    resolveActiveView();
  }, [finalSchemaInitState, resolveActiveView]);

  // initial filters
  useEffect(() => {
    if (!checkIsAllInit(finalSchemaInitState)) {
      return;
    }

    const finalSchema = getLastFinalSchemaLocalStore(companyId ?? 0, entity);

    setTableFilters(finalSchema?.filtersSchema);
  }, [companyId, entity, finalSchemaInitState, setTableFilters]);
};
